import { Controller } from '@hotwired/stimulus'
import TomSelect from "tom-select"

export default class extends Controller {
  static targets = ['select', 'nestedContainer', 'addButton', 'template', 'venueCounter', 'venueCount']
  static values = {
    venues: Array,
    selectedVenues: Array
  }

  connect() {
    this.nextIndex = 0
    this.pendingVenues = new Map() // Store pending venues with temp IDs
    this.initializeTomSelect()

    // Listen for form submissions
    this.element.addEventListener('submit', this.handleFormSubmit.bind(this))
  }

  initializeTomSelect() {
    // Préparer les options pour Tom Select (venues existantes)
    const venueOptions = this.venuesValue.map(venue => ({
      value: venue.id.toString(),
      text: venue.name,
      ...venue
    }))

    this.tomSelect = new TomSelect(this.selectTarget, {
      plugins: {
        remove_button: { title: 'Supprimer' }
      },
      create: false,
      maxItems: null,
      placeholder: 'Rechercher et sélectionner des venues...',
      searchField: ['text', 'city', 'address'],
      render: {
        option: (data, escape) => {
          const isPending = data.id && data.id.toString().startsWith('temp_')
          return `<div class="py-2 px-3 ${isPending ? 'bg-yellow-50' : ''}">
            <div class="flex items-center font-medium">
              ${escape(data.name || data.text)}
              ${isPending ? '<span class="px-2 py-1 ml-2 text-xs text-yellow-800 bg-yellow-200 rounded">En attente</span>' : ''}
            </div>
            <div class="text-sm text-gray-600">${escape(data.city || '')} ${data.address ? '- ' + escape(data.address) : ''}</div>
          </div>`
        },
        item: (data, escape) => {
          const isPending = data.id && data.id.toString().startsWith('temp_')
          return `<div class="flex items-center px-2 py-1 text-sm text-blue-800 bg-blue-100 rounded-md">
            ${escape(data.name || data.text)} ${data.city ? '(' + escape(data.city) + ')' : ''}
            ${isPending ? '<span class="ml-1 text-xs">⏳</span>' : ''}
          </div>`
        }
      }
    })

    // Select existing venues
    this.selectedVenuesValue.forEach(id => {
      this.tomSelect.addItem(id.toString())
    })

    // Initialize venue counter
    this.updateVenueCounter()

    // Listen for item removal
    this.tomSelect.on('item_remove', (value) => {
      this.handleItemRemoval(value)
    })

    // Listen for item addition to update counter
    this.tomSelect.on('item_add', () => {
      this.updateVenueCounter()
    })
  }

  addNestedVenue() {
    const template = this.templateTarget.innerHTML
    const index = this.nextIndex++

    // Replace placeholder with actual index
    const html = template.replace(/NEW_NESTED_VENUE/g, index)

    // Create container for this nested form
    const container = document.createElement('div')
    container.className = 'p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200 shadow-sm nested-venue-form'
    container.dataset.index = index
    container.innerHTML = html

    // Insert into the nested container
    this.nestedContainerTarget.appendChild(container)

    // Connect Stimulus controllers for the new element
    this.connectStimulusControllers(container)

    // Focus on first input
    const firstInput = container.querySelector('input[type="text"]')
    if (firstInput) {
      firstInput.focus()
    }

    // Update button state
    this.updateAddButtonText()
  }

  validateAndAddVenue(event) {
    event.preventDefault()

    const button = event.currentTarget
    const container = button.closest('.nested-venue-form')
    const index = container.dataset.index

    // Get form data
    const formData = this.getNestedFormData(container)

    // Clear previous errors
    this.clearErrors(container)

    // Validate
    const errors = this.validateVenueData(formData)
    if (Object.keys(errors).length > 0) {
      this.displayErrors(container, errors)
      return
    }

    // Create temporary ID for pending venue
    const tempId = `temp_${Date.now()}_${index}`

    // Create venue data for Tom Select
    const venueData = {
      id: tempId,
      name: formData.name,
      city: formData.city,
      address: formData.address,
      email: formData.email,
      phone: formData.phone,
      country: formData.country,
      text: formData.name // For Tom Select display
    }

    // Store in pending venues
    this.pendingVenues.set(tempId, venueData)

    // Add to Tom Select
    this.tomSelect.addOption(venueData)
    this.tomSelect.addItem(tempId)

    // Mark container with temp ID for later reference
    container.dataset.tempId = tempId

    // Hide the form but keep the data (don't remove from DOM)
    container.classList.add('hidden')
    container.classList.add('validated-venue')

    // Create confirmation card
    this.createVenueCard(tempId, venueData, container)

    // Update add button text to show we can add more
    this.updateAddButtonText()
  }

  cancelNestedVenue(event) {
    event.preventDefault()
    event.stopPropagation()

    const button = event.currentTarget
    const container = button.closest('.nested-venue-form')

    if (container) {
      // Hide first, then remove with delay like the other methods
      container.style.display = 'none'
      setTimeout(() => {
        container.remove()
      }, 100)
    } else {
    }

    this.updateAddButtonText()
  }

  handleItemRemoval(value) {

    if (value.startsWith('temp_')) {

      // Remove from pending venues
      this.pendingVenues.delete(value)

      // Find and hide/remove the hidden form with delay
      const container = this.element.querySelector(`.nested-venue-form[data-temp-id="${value}"]`)
      if (container) {
        // First hide it
        container.style.display = 'none'
        // Then remove it with a small delay
        setTimeout(() => {
          container.remove()
        }, 100)
      }

      // Find and hide/remove the confirmation card with delay
      const card = this.element.querySelector(`.venue-confirmation-card[data-temp-id="${value}"]`)
      if (card) {
        // First hide it
        card.style.display = 'none'
        // Then remove it with a small delay
        setTimeout(() => {
          card.remove()
        }, 100)
      }

      // Update UI immediately
      this.updateAddButtonText()
      this.updateVenueCounter()
    } else {
    }
  }

  getNestedFormData(container) {
    const inputs = container.querySelectorAll('input, select, textarea')
    const data = {}

    inputs.forEach(input => {
      const name = input.name.match(/\[(\w+)\]$/)?.[1]
      if (name) {
        data[name] = input.value.trim()
      }
    })

    return data
  }

  validateVenueData(data) {
    const errors = {}

    // Required fields
    if (!data.name) errors.name = 'Le nom est obligatoire'
    if (!data.address) errors.address = 'L\'adresse est obligatoire'
    if (!data.city) errors.city = 'La ville est obligatoire'
    if (!data.country) errors.country = 'Le pays est obligatoire'

    // Email validation
    if (data.email && !this.isValidEmail(data.email)) {
      errors.email = 'Format email invalide'
    }

    return errors
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  displayErrors(container, errors) {
    Object.keys(errors).forEach(field => {
      const input = container.querySelector(`input[name*="[${field}]"], select[name*="[${field}]"], textarea[name*="[${field}]"]`)
      if (input) {
        input.classList.add('border-red-500')

        // Add error message
        let errorDiv = input.parentNode.querySelector('.field-error')
        if (!errorDiv) {
          errorDiv = document.createElement('div')
          errorDiv.className = 'mt-1 text-sm text-red-500 field-error'
          input.parentNode.appendChild(errorDiv)
        }
        errorDiv.textContent = errors[field]
      }
    })
  }

  clearErrors(container) {
    // Remove error classes
    container.querySelectorAll('input, select, textarea').forEach(field => {
      field.classList.remove('border-red-500')
    })

    // Remove error messages
    container.querySelectorAll('.field-error').forEach(error => {
      error.remove()
    })
  }

  showSuccess(message) {
    const successDiv = document.createElement('div')
    successDiv.className = 'px-4 py-3 mb-4 text-green-700 bg-green-100 rounded border border-green-400 success-message'
    successDiv.textContent = message

    // Insert at the top of the nested container
    this.nestedContainerTarget.insertBefore(successDiv, this.nestedContainerTarget.firstChild)

    // Remove after 3 seconds
    setTimeout(() => {
      successDiv.remove()
    }, 3000)
  }

  createVenueCard(tempId, venueData, hiddenContainer) {
    const card = document.createElement('div')
    card.className = 'p-4 mb-4 bg-white rounded-lg border border-green-200 shadow-sm venue-confirmation-card'
    card.dataset.tempId = tempId

    card.innerHTML = `
      <div class="flex justify-between items-start">
        <div class="flex-1">
          <div class="flex items-center mb-2">
            <h4 class="text-lg font-medium text-gray-900">${this.escapeHtml(venueData.name)}</h4>
            <span class="inline-flex items-center px-2.5 py-0.5 ml-2 text-xs font-medium text-green-800 bg-green-100 rounded-full">
              En attente de création
            </span>
          </div>
          <div class="space-y-1 text-sm text-gray-600">
            <div class="flex items-center">
              <svg class="mr-2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
              </svg>
              <span>${this.escapeHtml(venueData.address)}, ${this.escapeHtml(venueData.city)}</span>
            </div>
            ${venueData.email ? `
              <div class="flex items-center">
                <svg class="mr-2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                <span>${this.escapeHtml(venueData.email)}</span>
              </div>
            ` : ''}
            ${venueData.phone ? `
              <div class="flex items-center">
                <svg class="mr-2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                </svg>
                <span>${this.escapeHtml(venueData.phone)}</span>
              </div>
            ` : ''}
          </div>
        </div>
        <div class="flex items-center ml-4 space-x-2">
          <button type="button"
                  data-action="click->nested-venues#editVenue"
                  data-temp-id="${tempId}"
                  class="inline-flex items-center p-2 text-sm text-gray-400 rounded hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  title="Modifier">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
            </svg>
          </button>
          <button type="button"
                  data-action="click->nested-venues#removeVenueCard"
                  data-temp-id="${tempId}"
                  class="inline-flex items-center p-2 text-sm text-red-400 rounded hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  title="Supprimer">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      </div>
    `

    // Insert the card before the nested container
    this.nestedContainerTarget.appendChild(card)
  }

  editVenue(event) {
    event.preventDefault()
    event.stopPropagation()

    const tempId = event.currentTarget.dataset.tempId
    const hiddenContainer = this.element.querySelector(`.nested-venue-form[data-temp-id="${tempId}"]`)
    const card = this.element.querySelector(`.venue-confirmation-card[data-temp-id="${tempId}"]`)

    if (hiddenContainer && card) {
      // Hide the card
      card.classList.add('hidden')

      // Show the form again
      hiddenContainer.classList.remove('hidden')

      // Focus on first input
      const firstInput = hiddenContainer.querySelector('input[type="text"]')
      if (firstInput) {
        firstInput.focus()
      }

      this.updateAddButtonText()
    }
  }

  removeVenueCard(event) {
    event.preventDefault()
    event.stopPropagation()

    const tempId = event.currentTarget.dataset.tempId

    // Remove from Tom Select
    this.tomSelect.removeItem(tempId)

    // Also call handleItemRemoval directly to ensure cleanup
    this.handleItemRemoval(tempId)

  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  updateAddButtonText() {
    const pendingCount = this.pendingVenues.size
    const allForms = this.element.querySelectorAll('.nested-venue-form')


    // Count forms manually - only count forms that are visible, not validated, AND empty
    let formsBeingFilledCount = 0
    allForms.forEach((form, index) => {
      const isHidden = form.classList.contains('hidden')
      const isValidated = form.classList.contains('validated-venue')
      const isDisplayNone = form.style.display === 'none'

      // Check if form has any content (name field specifically)
      const nameInput = form.querySelector('input[name*="[name]"]')
      const hasContent = nameInput && nameInput.value.trim().length > 0


      // Only count empty, visible, non-validated forms as "blocking"
      // Also exclude forms that are hidden with display: none
      if (!isHidden && !isValidated && !hasContent && !isDisplayNone) {
        formsBeingFilledCount++
      }
    })


    // Only block if there are forms currently being filled (not validated yet)
    if (formsBeingFilledCount > 0) {
      // Update button content with icon
      this.addButtonTarget.innerHTML = `
        <svg class="mr-2 w-5 h-5 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        Terminer cette venue avant d'en ajouter une autre
      `
      this.addButtonTarget.disabled = true
      this.addButtonTarget.classList.add('opacity-50', 'cursor-not-allowed', 'bg-gray-400')
      this.addButtonTarget.classList.remove('bg-blue-600', 'hover:bg-blue-700')
    } else {
      // Reset button to normal state
      const buttonText = pendingCount > 0 ? 'Créer une autre venue' : 'Créer une nouvelle venue'
      this.addButtonTarget.innerHTML = `
        <svg class="mr-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
        </svg>
        ${buttonText}
      `
      this.addButtonTarget.disabled = false
      this.addButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed', 'bg-gray-400')
      this.addButtonTarget.classList.add('bg-blue-600', 'hover:bg-blue-700')
    }
  }

  updateVenueCounter() {
    if (this.hasVenueCountTarget) {
      const selectedCount = this.tomSelect ? this.tomSelect.items.length : 0
      this.venueCountTarget.textContent = selectedCount

      // Update counter badge styling based on count
      const counterElement = this.venueCounterTarget
      if (selectedCount === 0) {
        counterElement.className = 'px-3 py-1 text-sm text-gray-600 bg-gray-100 rounded-full'
      } else {
        counterElement.className = 'px-3 py-1 text-sm font-medium text-blue-700 bg-blue-100 rounded-full'
      }
    }
  }

  handleFormSubmit(event) {
  }

  connectStimulusControllers(element) {
    // Get the Stimulus application from window or use a more direct approach
    const app = window.Stimulus || this.application

    if (app) {
      // Use setTimeout to ensure the DOM is ready before connecting controllers
      setTimeout(() => {
        try {
          // Force Stimulus to re-scan the new element
          app.start()
        } catch (error) {
          console.warn('Could not restart Stimulus application', error)
        }
      }, 50)
    }

    // Also dispatch a mutation event that Stimulus may listen to
    const observer = new MutationObserver(() => {
      // Disconnect after first run
      observer.disconnect()
    })

    observer.observe(element, {
      childList: true,
      subtree: true
    })
  }

}
